import { css, styled } from 'next-yak';
import type { ElementType, HTMLAttributes } from 'react';
import { forwardRef } from 'react';

import { Link } from '@xing-com/link';
import {
  cornerRadius3XL,
  cornerRadiusXL,
  cornerRadiusXXL,
  dropShadowS,
  motionEasingEnter,
  motionTimeM,
  scale005,
  scale010,
  scale020,
  scale080,
  space3XL,
  spaceL,
  spaceXL,
  spaceXXL,
  spaceXXS,
  xdlColorControlBorderSoft,
  xdlColorControlFill,
  xdlColorFocusRing,
  xdlColorModalBorder,
  xdlColorSelected,
} from '@xing-com/tokens';

import type { CardPaddings, CardSizes, CardVariant } from './card.types';

type CardTProps = {
  $autoposition?: boolean;
  $hasLink?: boolean;
  $maxHeight?: string;
  $maxWidth?: string;
  $minHeight?: string;
  $minWidth?: string;
  $padding?: CardPaddings;
  $selected?: boolean;
  $size?: CardSizes;
  $variant: CardVariant;
};

type CardLinkTProps = {
  $size?: CardSizes;
};

type CardElementProps = HTMLAttributes<HTMLElement> & {
  as?: ElementType;
};

const CardElement = forwardRef<HTMLElement, CardElementProps>(
  ({ as: Component = 'div', ...props }, ref) => (
    <Component {...props} ref={ref} />
  )
);

type CardContentTProps = {
  $padding?: CardPaddings;
  $size?: CardSizes;
};

/** Paddings */
export const paddings = {
  regular: 'regular',
  wide: 'wide',
  none: 'none',
};

/** Sizes */
export const sizes = {
  small: {
    corners: css`
      border-radius: ${cornerRadiusXL};
    `,
    cornersExtended: css`
      flex-grow: 1;
      overflow: hidden;
      border-radius: calc(${cornerRadiusXL} - ${scale010});
    `,
    spacing: {
      [paddings.regular]: css`
        padding: ${spaceL};
      `,
      [paddings.wide]: css`
        padding-block: ${spaceL};
        padding-inline: ${spaceXL};
      `,
      [paddings.none]: css`
        padding: 0;
      `,
    },
  },
  medium: {
    corners: css`
      border-radius: ${cornerRadiusXXL};
    `,
    cornersExtended: css`
      flex-grow: 1;
      overflow: hidden;
      border-radius: calc(${cornerRadiusXXL} - ${scale010});
    `,
    spacing: {
      [paddings.regular]: css`
        padding: ${spaceXL};
      `,
      [paddings.wide]: css`
        padding-block: ${spaceXL};
        padding-inline: ${spaceXXL};
      `,
      [paddings.none]: css`
        padding: 0;
      `,
    },
  },
  large: {
    corners: css`
      border-radius: ${cornerRadius3XL};
    `,
    cornersExtended: css`
      flex-grow: 1;
      overflow: hidden;
      border-radius: calc(${cornerRadius3XL} - ${scale010});
    `,
    spacing: {
      [paddings.regular]: css`
        padding: ${spaceXXL};
      `,
      [paddings.wide]: css`
        padding-block: ${spaceXXL};
        padding-inline: ${space3XL};
      `,
      [paddings.none]: css`
        padding: 0;
      `,
    },
  },
};

export const variants = {
  default: css`
    border-color: ${xdlColorControlBorderSoft};
    border-width: ${scale010};
  `,
  elevated: css`
    border-color: ${xdlColorModalBorder};
    border-width: ${scale010};
    box-shadow: ${dropShadowS};
  `,
};

// *** Components ***
export const Card = styled(CardElement)<CardTProps>`
  background-color: ${xdlColorControlFill};
  border-style: solid;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${scale080};
  position: relative;
  transition-duration: ${motionTimeM};
  transition-property: transform, box-shadow;
  transition-timing-function: ${motionEasingEnter};

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return sizes.small.corners;
      case 'medium':
        return sizes.medium.corners;
      case 'large':
        return sizes.large.corners;
      default:
        return $size satisfies undefined;
    }
  }}

  ${({ $size, $padding }) => {
    switch ($size) {
      case 'small':
        switch ($padding) {
          case 'regular':
            return sizes.small.spacing.regular;
          case 'wide':
            return sizes.small.spacing.wide;
          case 'none':
            return sizes.small.spacing.none;
          default:
            return $padding satisfies undefined;
        }
      case 'medium':
        switch ($padding) {
          case 'regular':
            return sizes.medium.spacing.regular;
          case 'wide':
            return sizes.medium.spacing.wide;
          case 'none':
            return sizes.medium.spacing.none;
          default:
            return $padding satisfies undefined;
        }
      case 'large':
        switch ($padding) {
          case 'regular':
            return sizes.large.spacing.regular;
          case 'wide':
            return sizes.large.spacing.wide;
          case 'none':
            return sizes.large.spacing.none;
          default:
            return $padding satisfies undefined;
        }
      default:
        return $size satisfies undefined;
    }
  }}

  ${({ $variant }) => {
    switch ($variant) {
      case 'default':
        return variants.default;
      case 'elevated':
        return variants.elevated;
      default:
        return $variant satisfies undefined;
    }
  }}

  ${({ $hasLink }) => {
    if (!$hasLink) return;
    return css`
      position: relative;

      &:hover,
      &:focus,
      &:focus-within {
        &::after {
          border-color: ${xdlColorSelected};
          border-radius: inherit;
          border-style: solid;
          border-width: calc(${scale010} + ${scale005});
          content: '';
          inset: -${spaceXXS};
          pointer-events: none;
          position: absolute;
        }
      }
    `;
  }}

  ${({ $autoposition }) => {
    if (!$autoposition) return;
    return css`
      & a,
      & button {
        position: relative;
        z-index: 9999;
      }
    `;
  }}

  ${({ $selected }) => {
    if (!$selected) return;
    return css`
      &&& {
        border-color: ${xdlColorSelected};
        border-width: ${scale010};
      }
    `;
  }}

  ${({ $maxHeight }) => {
    if (!$maxHeight) return;
    return css`
      max-height: ${() => $maxHeight};
    `;
  }}

  ${({ $maxWidth }) => {
    if (!$maxWidth) return;
    return css`
      max-width: ${() => $maxWidth};
    `;
  }}

  ${({ $minHeight }) => {
    if (!$minHeight) return;
    return css`
      min-height: ${() => $minHeight};
    `;
  }}

  ${({ $minWidth }) => {
    if (!$minWidth) return;
    return css`
      min-width: ${() => $minWidth};
    `;
  }}
`;

export const CardContent = styled.div<CardContentTProps>`
  display: flex;
  flex-direction: column;

  ${({ $padding, $size }) => {
    if ($padding !== 'none') return;
    switch ($size) {
      case 'small':
        return sizes.small.cornersExtended;
      case 'medium':
        return sizes.medium.cornersExtended;
      case 'large':
        return sizes.large.cornersExtended;
      default:
        return $size satisfies undefined;
    }
  }}
`;

export const CardLink = styled(Link)<CardLinkTProps>`
  height: 100%;
  inset: 0;
  outline-offset: ${scale020};
  width: 100%;

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return sizes.small.cornersExtended;
      case 'medium':
        return sizes.medium.cornersExtended;
      case 'large':
        return sizes.large.cornersExtended;
      default:
        return $size satisfies undefined;
    }
  }}

  &:focus {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: calc(${spaceXXS} + ${scale010});
  }

  && {
    position: absolute;
    z-index: 0;
  }
`;
